import * as React from 'react';
import { isDefined, noop, TrendLine } from 'react-financial-charts';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { getValueFromOverride } from '@/utils/utils';
import type { TrendLineProps } from './TrendLine';
import type { TrendLineDragData } from './Wrapper/EachTrendLine';
import type { RenderNode, MoreProps } from '@/types/commontypes';
import { EachVertLine } from './Wrapper/EachVertLine';
import type { ChartId, TransientBroadcastDragOverride } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import { findYpoint } from './Wrapper/InteractiveStraightLineWithoutPoint';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';
import { toHorzVertLineRFCProps, RFC_RENDER_MODE } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import type { VertLineRenderNode } from '@/types/commontypes';
import type { ChartUIStoreInstance } from '../../../stores/chartUIStore';

/**
 * VertLine 전용 prop 인터페이스.
 *
 * VertLine은 `chartId_common`에 그려져 여러 chart panel에 걸쳐 있으므로,
 * React state로는 cross-panel 동기화가 어렵다. 대신 chartUIStore의 transient
 * channel을 broadcast 버스로 사용한다 — 이를 위한 `onOverride`/`overrideData`/
 * `chartUIStore` 세 prop이 필요하다. 다른 도형(TrendLine/Rectangle 등)은
 * 자기 컴포넌트의 state.override만으로 충분하므로 이 prop들을 받지 않는다.
 */
export interface VertLineProps extends TrendLineProps {
  readonly onOverride?: (overrideData: TransientBroadcastDragOverride) => void;
  readonly overrideData?: TransientBroadcastDragOverride;
  readonly chartUIStore?: ChartUIStoreInstance;
}

interface VertLineState {
  trends?: RenderNode | RenderNode[] | null;
  lineInteractiveCursor?: string;
}

export class VertLine extends React.Component<VertLineProps, VertLineState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // 드래그 중 override 데이터를 인스턴스 필드에 저장.
  // handleDragLineComplete에서 props.overrideData 대신 이 필드를 사용한다.
  private _latestOverride: TransientBroadcastDragOverride = {};

  // transient override channel 구독 해제 함수
  private _unsubscribeOverride?: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!VertLine._cachedDefaultProps) {
      VertLine._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        trends: [],
      };
    }
    return VertLine._cachedDefaultProps;
  }
  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved = false;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: VertLineProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('trends').bind(this);

    this.state = {};
  }

  public componentDidMount() {
    // chartUIStore가 prop으로 전달되면 transient override channel을 구독.
    // override 변경 시 forceUpdate로 이 컴포넌트만 re-render (ShapeRender는 건드리지 않음).

    const { chartUIStore: store } = this.props;
    if (store?.subscribeOverride) {
      this._unsubscribeOverride = store.subscribeOverride(() => {
        this.forceUpdate();
      });
    }
  }

  public componentWillUnmount() {
    this._unsubscribeOverride?.();
  }

  public render() {
    const {
      currentPositionstrokeOpacity,
      currentPositionRadius = TrendLine.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      shouldDisableSnap,
      snap,
      snapTo,
      trends,
      direction,
      onDoubleClick,
      onOverride,
      sharedState,
      isAnyShapeEnabled,
    } = this.props;

    // transient channel에서 최신 override를 읽는다.
    // ShapeRender가 re-render하지 않아도 forceUpdate로 이 render()가 호출되므로 항상 최신값.

    const { chartUIStore: store } = this.props;
    const liveOverride = store?.getTransientOverride?.() ?? {};

    return (
      <g>
        {trends?.map((each, idx) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toHorzVertLineRFCProps(each as VertLineRenderNode) as Record<string, unknown> & {
            strokeStyle: string;
            strokeWidth: number;
            strokeDasharray: string;
            edgeStroke: string;
            edgeFill: string;
            edgeStrokeWidth: number;
            r: number;
          };
          const eachStartEnd = each as typeof each & { start?: [number, number]; end?: [number, number] };
          if (each.hidden || !eachStartEnd.start || !eachStartEnd.end) return null;
          const globalOverride = liveOverride;

          const eachAppearanceAny = eachAppearance as Record<string, unknown>;
          const x1 =
            globalOverride?.uuid === each.uuid
              ? globalOverride?.x1Value
              : getValueFromOverride(liveOverride, each.uuid, 'x1Value', eachStartEnd.start[0]);

          const y1 =
            globalOverride?.uuid === each.uuid
              ? globalOverride?.y1Value
              : getValueFromOverride(liveOverride, each.uuid, 'y1Value', eachStartEnd.start[1]);

          const x2 =
            globalOverride?.uuid === each.uuid
              ? globalOverride?.x2Value
              : getValueFromOverride(liveOverride, each.uuid, 'x2Value', eachStartEnd.end?.[0]);

          const y2 =
            globalOverride?.uuid === each.uuid
              ? globalOverride?.y2Value
              : getValueFromOverride(liveOverride, each.uuid, 'y2Value', eachStartEnd.end?.[1]);

          if (x1 == null || y1 == null || x2 == null || y2 == null) return null;

          // Return the EachTrendLine component with merged commonProps and additionalProps
          return (
            <EachVertLine
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              uuid={each.uuid}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={RFC_RENDER_MODE[each.type] as 'XLINE' | 'RAY' | 'LINE'}
              selected={(each as unknown as { selected?: boolean }).selected}
              x1Value={x1}
              y1Value={y1}
              x2Value={x2}
              y2Value={y2}
              strokeStyle={eachAppearance.strokeStyle}
              strokeWidth={eachAppearance.strokeWidth}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeOpacity={eachAppearanceAny.strokeOpacity as number}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeDasharray={eachAppearance.strokeDasharray as never}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              edgeStrokeWidth={eachAppearance.edgeStrokeWidth}
              r={eachAppearance.r}
              direction={direction}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="big-declined-arrow-cursor"
              lineInteractiveCursor={'react-financial-charts-pointer-cursor'}
              onSelect={this.handleSelect}
              onDragStart={this.handleDragStart}
              onHover={this.handleHover}
              locked={each.locked ?? false}
              onDoubleClick={onDoubleClick}
              leftArrow={false}
              rightArrow={false}
              overrideData={liveOverride}
              onOverride={onOverride}
              sharedState={sharedState ?? {}}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleStart as unknown as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void
          }
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { trends } = this.props;
    const newTrends = trends.map(trends => ({
      ...trends,
      selected: trends.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newTrends, moreProps);
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { trends, onComplete, onOverride } = this.props;

    const { chartUIStore: handleStartStore } = this.props;
    const overrideData = handleStartStore?.getTransientOverride?.() ?? {};
    const lastProps = getActiveTemplateNode(ShapeType.VERT_LINE);
    const defaults = defaultShapePropertyValues[ShapeType.VERT_LINE];
    const initial = { ...defaults, ...lastProps };
    const createTrend = (start: number[], end: number[], additionalProps: Record<string, unknown>) => {
      return [
        ...trends.map(d => ({ ...d, selected: false })),
        {
          ...initial,
          ...additionalProps,
          start,
          end,
          selected: true,
          type: ShapeType.VERT_LINE,
        },
      ];
    };
    const xValue = xyValue[0];

    const { y1, y2 } = findYpoint(moreProps);
    const commonChartId = 'chartId_common';
    const shapeUuid = crypto.randomUUID();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const newTrends = createTrend([xValue, y1], [xValue, y2], {
      xValue,
      chartId: commonChartId,
      uuid: shapeUuid,
      hidden: false,
      type: ShapeType.VERT_LINE,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      originChartId: moreProps.chartId,
    }) as RenderNode[];
    if (isDefined(overrideData) && isDefined(onOverride)) {
      const lastTrend = newTrends[newTrends.length - 1];
      const startOverride: TransientBroadcastDragOverride = {
        chartId: commonChartId,
        hover: undefined,
        uuid: lastTrend?.uuid,
        x1Value: xValue,
        y1Value: y1,
        x2Value: xValue,
        y2Value: y2,
      };
      this._latestOverride = startOverride;
      onOverride(startOverride);
    }
    this.setState({ trends: newTrends }, () => onComplete?.(e, newTrends, moreProps));
  };

  private readonly handleDragLineComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { trends, onDragComplete, hoverId, onOverride } = this.props;
    // _latestOverride는 handleDragLine/handleDragStart에서 갱신되므로 항상 최신 값이다.
    const overrideData = this._latestOverride;
    // @ts-expect-error
    if (!isDefined(overrideData) && overrideData?.uuid !== hoverId) return;
    const { x1Value, x2Value, y1Value, y2Value } = overrideData;
    if (!isDefined(x1Value) || !isDefined(y1Value) || !isDefined(x2Value) || !isDefined(y2Value)) {
      return;
    }
    this.mouseMoved = false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const updatedTrends = trends.map((each, _idx) =>
      each.uuid === overrideData?.uuid
        ? {
            ...each,
            xValue: overrideData?.x1Value, // shapeToCoordinates가 xValue를 읽으므로 반드시 갱신
            start: [overrideData?.x1Value, overrideData?.y1Value],
            end: [overrideData?.x2Value, overrideData?.y2Value],
            selected: true,
            alwaysShow: true,
            updatedAt: Date.now(),
          }
        : {
            ...each,
            selected: false,
          },
    ) as RenderNode[];
    // Clear global override
    this._latestOverride = {};
    if (onOverride) onOverride?.({});
    this.setState({ trends: updatedTrends }, () => {
      onDragComplete?.(e, updatedTrends, moreProps);
    });
  };

  private readonly handleDragLine = (_: React.MouseEvent, uuid: string | undefined, newXYValue: TrendLineDragData) => {
    const { onOverride } = this.props;
    const chartId = 'chartId_common';
    if (!uuid) return;
    const updatedOverride = {
      uuid,
      chartId, // important
      hover: uuid,
      ...newXYValue,
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    this._latestOverride = updatedOverride as unknown as TransientBroadcastDragOverride;
    // Update global state so other charts can sync
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    onOverride?.(updatedOverride as unknown as TransientBroadcastDragOverride);
  };

  private readonly handleDragStart = (uuid: string) => {
    const trend = this.props.trends.find(t => t.uuid === uuid);
    if (!trend) return;
    const { onOverride } = this.props;
    const chartId = 'chartId_common' as ChartId;

    const trendStartEnd = trend as typeof trend & { start?: [number, number]; end?: [number, number] };
    const overridePayload = {
      uuid,
      chartId,
      x1Value: trendStartEnd.start?.[0],
      y1Value: trendStartEnd.start?.[1],
      x2Value: trendStartEnd.end?.[0],
      y2Value: trendStartEnd.end?.[1],
      hover: undefined,
    };

    this._latestOverride = overridePayload;
    // Store globally
    if (onOverride) {
      onOverride(overridePayload);
    }
  };
}
